import React from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useTradeContext } from "../hooks/useTradeContext";

interface TradeContextDialogProps {
  tradeId: number | null;
  open: boolean;
  onClose: () => void;
}

const formatCurrency = (value?: number) =>
  value !== undefined ? `$${value.toFixed(2)}` : "N/A";

const formatPercent = (value?: number) =>
  value !== undefined ? `${value.toFixed(2)}%` : "N/A";

const formatTimestamp = (value?: string | Date) =>
  value ? new Date(value).toLocaleString() : "-";

const sentimentColor = (sentiment?: string) => {
  switch (sentiment) {
    case "BULLISH":
      return "success";
    case "BEARISH":
      return "error";
    default:
      return "default";
  }
};

const getChangeColor = (value?: number) => {
  if (value === undefined) return "text.secondary";
  return value >= 0 ? "success.main" : "error.main";
};

const TradeContextDialog: React.FC<TradeContextDialogProps> = ({
  tradeId,
  open,
  onClose,
}) => {
  const { tradeContext, loading, error } = useTradeContext(tradeId);
  const trade = tradeContext?.trade;
  const priceComparison = tradeContext?.priceComparison;
  const marketConditions = tradeContext?.marketConditions;
  const scenarios = tradeContext?.whatIfScenarios;
  const pnlImpact = tradeContext?.pnlImpact;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="trade-context-dialog"
    >
      <DialogTitle id="trade-context-dialog">
        Trade Context{trade ? ` - ${trade.symbol}` : ""}
        {trade?.side && (
          <Typography component="span" sx={{ ml: 1 }} variant="subtitle2">
            ({trade.side})
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Typography color="error">
            {error.message || "Unable to load trade context"}
          </Typography>
        )}
        {!loading && !error && !tradeContext && (
          <Typography color="text.secondary">
            No contextual data available for this trade yet.
          </Typography>
        )}
        {tradeContext && (
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Trade Snapshot
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Entry Price
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(tradeContext.priceAtEntry?.price)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatTimestamp(tradeContext.priceAtEntry?.timestamp)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Current Price
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(tradeContext.currentPrice?.price)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Updated{" "}
                    {formatTimestamp(tradeContext.currentPrice?.timestamp)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Pricing Performance
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Price Change
                  </Typography>
                  <Typography
                    variant="h6"
                    color={getChangeColor(priceComparison?.priceChange)}
                  >
                    {priceComparison
                      ? `${formatCurrency(priceComparison.priceChange)} (${formatPercent(
                          priceComparison.priceChangePercent,
                        )})`
                      : "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Unrealized PnL
                  </Typography>
                  <Typography
                    variant="h6"
                    color={getChangeColor(pnlImpact?.unrealizedPnl)}
                  >
                    {pnlImpact
                      ? `${formatCurrency(pnlImpact.unrealizedPnl)} (${formatPercent(
                          pnlImpact.pnlPercent,
                        )})`
                      : "N/A"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Position Value:{" "}
                    {formatCurrency(pnlImpact?.totalPositionValue)}
                  </Typography>
                </Grid>
              </Grid>
              {priceComparison && (
                <Typography variant="body2" color="text.secondary">
                  Entry - Current: {formatCurrency(priceComparison.entryPrice)}{" "}
                  {"->"} {formatCurrency(priceComparison.currentPrice)}
                </Typography>
              )}
            </Box>

            <Divider />

            {marketConditions && (
              <Box>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <Typography variant="subtitle2">
                    Market Conditions at Entry
                  </Typography>
                  {marketConditions.marketSentiment && (
                    <Chip
                      size="small"
                      label={marketConditions.marketSentiment}
                      color={sentimentColor(marketConditions.marketSentiment)}
                      variant="outlined"
                    />
                  )}
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Volatility
                    </Typography>
                    <Typography variant="body1">
                      {formatPercent(marketConditions.volatility)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Volume
                    </Typography>
                    <Typography variant="body1">
                      {marketConditions.volume?.toLocaleString() || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      RSI
                    </Typography>
                    <Typography variant="body1">
                      {marketConditions.rsi?.toFixed(2) ?? "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Moving Avg
                    </Typography>
                    <Typography variant="body1">
                      {formatCurrency(marketConditions.movingAverage)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Support
                    </Typography>
                    <Typography variant="body1">
                      {formatCurrency(marketConditions.support)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Resistance
                    </Typography>
                    <Typography variant="body1">
                      {formatCurrency(marketConditions.resistance)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Recorded
                    </Typography>
                    <Typography variant="body1">
                      {formatTimestamp(marketConditions.timestamp)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                What-if Scenarios
              </Typography>
              {scenarios && scenarios.length > 0 ? (
                <Stack spacing={1}>
                  {scenarios.map((scenario, index) => {
                    const isPositiveScenario =
                      scenario.pnl !== undefined && scenario.pnl >= 0;
                    return (
                      <Box
                        key={`${scenario.scenario}-${index}`}
                        sx={{
                          borderRadius: 1,
                          border: 1,
                          borderColor: isPositiveScenario
                            ? "success.light"
                            : "error.light",
                          p: 1,
                        }}
                      >
                        <Typography variant="subtitle2">
                          {scenario.scenario}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {scenario.description}
                        </Typography>
                        <Typography
                          variant="caption"
                          color={getChangeColor(scenario.pnl)}
                        >
                          Entry: {formatCurrency(scenario.entryPrice)} • Exit:{" "}
                          {formatCurrency(scenario.exitPrice)} • PnL:{" "}
                          {formatCurrency(scenario.pnl)} (
                          {formatPercent(scenario.pnlPercent)})
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No scenarios were generated for this trade.
                </Typography>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TradeContextDialog;
